import React from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ShieldCheck, HeartHandshake, AlertCircle, Sparkles, BookOpen } from "lucide-react";
import "./bridge.css";
export default function WhenDistressing() {
  return <section id="bridge-support" aria-labelledby="bridge-support-heading" className="scroll-mt-24 pt-10 sm:pt-14">
      <div className="ql-glass-strong rounded-2xl p-5 sm:p-8 md:p-10 border-primary/30 max-w-4xl mx-auto space-y-6">
        {/* Eyebrow and Section Header */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.14em] uppercase text-primary">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span>Step 4 of 4 · Reflection & Support</span>
          </div>

          <h3 id="bridge-support-heading" tabIndex={-1} className="font-serif text-2xl sm:text-3xl font-bold text-foreground outline-none">
            When Daydreaming Feels Distressing or Hard to Steer
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Imagination is a natural, deeply human capacity. Most people daydream, construct inner stories, and experiment with alternate lives. Here is a calm, non-judgmental look at when it feels helpful, when it feels overwhelming, and how to find balance.
          </p>
        </div>

        {/* Educational Disclosure Accordion */}
        <Accordion type="multiple" className="w-full space-y-3">
          <AccordionItem value="nature-of-imagination" className="border border-border/70 rounded-xl bg-card/50 overflow-hidden">
            <AccordionTrigger className="dtr-accordion-trigger text-left font-serif text-sm sm:text-base text-foreground font-semibold">
              <span className="flex items-center gap-2.5">
                <Sparkles className="size-4 text-primary shrink-0" />
                <span>Imagination is common, enjoyable, and often useful</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="dtr-accordion-content space-y-3">
              <p>
                Building alternate scenarios in your mind is completely normal. For writers, artists, scientists, and thinkers, imaginative drift is often the birthplace of creative work, problem-solving, and emotional processing.
              </p>
              <p>
                An imagined life can offer rest from daily pressure, give voice to unexplored sides of your personality, or highlight core values—such as a desire for autonomy, kinship, or craft—that you haven’t yet found space for in your physical routine.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="when-concerning" className="border border-border/70 rounded-xl bg-card/50 overflow-hidden">
            <AccordionTrigger className="dtr-accordion-trigger text-left font-serif text-sm sm:text-base text-foreground font-semibold">
              <span className="flex items-center gap-2.5">
                <AlertCircle className="size-4 text-chart-3 shrink-0" />
                <span>When immersive dreaming may feel concerning</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="dtr-accordion-content space-y-3">
              <p>
                Imagination becomes exhausting or distressing when it stops feeling like a voluntary choice. People describe reaching a point where:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm">
                <li>Inner worlds are hard to stop or pull away from when you need to focus.</li>
                <li>Hours spent daydreaming cut into restorative sleep, daytime obligations, study, or work.</li>
                <li>Imagined connections begin to substitute for in-person friendships or cause real-world isolation.</li>
                <li>Returning to the physical world brings feelings of disappointment, shame, or self-criticism.</li>
              </ul>
              <p>
                If this sounds familiar, you are not alone, and it is not a personal failure or lack of discipline. It often simply means your inner world has been carrying emotional needs that aren't being met in daily life.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="practical-grounding" className="border border-border/70 rounded-xl bg-card/50 overflow-hidden">
            <AccordionTrigger className="dtr-accordion-trigger text-left font-serif text-sm sm:text-base text-foreground font-semibold">
              <span className="flex items-center gap-2.5">
                <BookOpen className="size-4 text-primary shrink-0" />
                <span>Small, practical things people try</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="dtr-accordion-content space-y-3">
              <p>
                Rather than trying to forcefully suppress your imagination, practical approaches focus on gentle boundaries and sensory grounding:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm">
                <li>
                  <strong>Designate an intentional daydreaming window:</strong> Set a gentle alarm for 15 or 20 minutes to enjoy your stories freely, rather than letting them blur unpredictably into the workday.
                </li>
                <li>
                  <strong>Physical grounding cues:</strong> Wash your face with cold water, stretch, change rooms, or hold a warm mug when you notice attention drifting away from what you wanted to do.
                </li>
                <li>
                  <strong>Externalize the narrative:</strong> Write down scenes or draw characters in a dedicated notebook. Giving thoughts a physical page often helps the brain feel that the story has been captured and safely put down.
                </li>
                <li>
                  <strong>Take one tiny physical action:</strong> Connect the longing in the fantasy to a two-minute real-world task—sending a message, opening a sketchbook, or stepping outdoors.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="professional-support" className="border border-border/70 rounded-xl bg-card/50 overflow-hidden">
            <AccordionTrigger className="dtr-accordion-trigger text-left font-serif text-sm sm:text-base text-foreground font-semibold">
              <span className="flex items-center gap-2.5">
                <HeartHandshake className="size-4 text-primary shrink-0" />
                <span>Speaking with a professional</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="dtr-accordion-content space-y-3">
              <p>
                If daydreaming feels disruptive, distressing, or compulsive, talking with a licensed mental-health professional or medical doctor is a completely reasonable and supportive step.
              </p>
              <p className="font-semibold text-foreground">
                You do not need to give up your imagination or stop being creative to ask for support.
              </p>
              <p>
                Therapy can help explore the underlying reasons your mind retreats into fantasy—such as chronic stress, loneliness, past trauma, or neurodivergent traits like ADHD—and help you build strategies that preserve your creativity while protecting your everyday well-being.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Essential Disclaimers and Crisis Information Banner */}
        <div className="p-4 sm:p-5 rounded-xl border border-border/80 bg-background/80 space-y-3 text-xs text-muted-foreground backdrop-blur-md">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-semibold text-foreground">Informational & Educational Scope</p>
              <p className="leading-relaxed">
                This platform is an educational science and storytelling exploration. Nothing here constitutes clinical medical advice, psychiatric assessment, or psychotherapy. Research on immersive and intense daydreaming is still actively developing, and it is not presented here as a formal diagnosis.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 border-t border-border/50 pt-3">
            <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-semibold text-foreground">Crisis Support</p>
              <p className="leading-relaxed">
                If you are in immediate distress, experiencing overwhelming feelings, or feel unsafe, please reach out to your local emergency medical service or contact a crisis line in your country. Compassionate, confidential support is available around the clock.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}